import './Workspace.scss';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { Splitter } from 'components/Splitter/Splitter';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { getNodeTypes } from 'redux/slices/lists/nodeTypesSlice';
import { WorkspaceModel } from './WorkspaceModel';
import { WorkspaceTree } from './WorkspaceTree';
import { WorkspaceContents } from './WorkspaceContents';
import { WorkspaceToolbar } from './WorkspaceToolbar';
import { useWorkspaceUrlSync } from './useWorkspaceUrlSync';

const MIN_TREE_WIDTH = 220;
const MAX_TREE_WIDTH = 560;
const DEFAULT_TREE_WIDTH = 320;

/**
 * The Workspace page (#887): a toolbar spanning the width, then a container tree
 * (left) and the merged contents of the selected container (right), split by a
 * draggable vertical splitter. A single WorkspaceModel owns the state; this
 * component wires it to the URL and the node-types LOV (for node-definition
 * deprecation) and renders the pure-observer panes.
 */
const WorkspacePage = observer(() => {
  const model = useMemo(() => new WorkspaceModel(), []);
  useWorkspaceUrlSync(model);
  const [treeWidth, setTreeWidth] = useState(DEFAULT_TREE_WIDTH);

  const dispatch = useAppDispatch();
  const knownNodeTypes = useAppSelector((state) => state.listOfNodeTypes?.node_types ?? []);
  const nodeTypesStatus = useAppSelector((state) => state.listOfNodeTypes?.status);
  useEffect(() => {
    if (!nodeTypesStatus) dispatch(getNodeTypes());
  }, [dispatch, nodeTypesStatus]);

  return (
    <div className="workspace-page">
      <WorkspaceToolbar model={model} />
      <div className="workspace-body">
        <div className="workspace-tree-pane" style={{ width: treeWidth }}>
          <WorkspaceTree model={model} />
        </div>
        <Splitter size={treeWidth} onResize={setTreeWidth} min={MIN_TREE_WIDTH} max={MAX_TREE_WIDTH} side="before" ariaLabel="Resize the tree pane" />
        <div className="workspace-contents-pane">
          <WorkspaceContents model={model} knownNodeTypes={knownNodeTypes} />
        </div>
      </div>
    </div>
  );
});

export default WorkspacePage;
